import React, { useCallback, useEffect, useState, useRef } from 'react'
import {
    Toast,
    Dialog,
    ImageViewer,
    Empty,
    Popup,
} from 'antd-mobile'
import './index.css'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { UserOutline, HistogramOutline, UserSetOutline, ChatWrongOutline, CalendarOutline, CheckOutline, UserContactOutline } from 'antd-mobile-icons'
import { useAuth } from '../../app/providers/AuthProvider';
import { memberApi } from '../../features/member/api/memberApi';
import { seasonApi } from '../../features/season/api/seasonApi';
import { happyEventApi } from '../../features/happy-event/api/happyEventApi';
import Icon from '../../icon';
import axios from 'axios';
import API_ROUTES from '../../api';

const formatProfileBirthday = (birthday) => {
    if (!birthday) return '未填写';
    return String(birthday).slice(0, 10).replace(/-/g, '.');
};

export default function MemberInfo() {
    const location = useLocation();
    const navigate = useNavigate();
    const { memberId } = useParams();
    const scrollerRef = useRef(null);
    const { memberInfo: adminInfo, signOut } = useAuth();

    const stateUserId = location.state?.userId;
    const userId = memberId || stateUserId;

    const [userInfo, setUserInfo] = useState(null);
    const [memberImages, setMemberImages] = useState([]);
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    const [yearsVisible, setYearsVisible] = useState(false);
    const [yearsButton, setYearsButton] = useState({});
    const [yearsAction, setYearsAction] = useState([]);
    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [adminVisible, setAdminVisible] = useState(false);
    const [attendanceVisible, setAttendanceVisible] = useState(false);
    const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
    const [selectedAttendanceMonths, setSelectedAttendanceMonths] = useState(() => {
        const now = new Date();
        const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return [`${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(2, '0')}`];
    });
    const [attendanceExporting, setAttendanceExporting] = useState(false);
    const [mediaPreviewItem, setMediaPreviewItem] = useState(null);
    const [relatedHappyEvents, setRelatedHappyEvents] = useState([]);
    const [relatedHappyTotal, setRelatedHappyTotal] = useState(0);
    const now = new Date();
    const currentAttendanceMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    useEffect(() => {
        // 兼容旧入口，并立即替换为可刷新、可分享的正式详情地址。
        if (!memberId && stateUserId) {
            navigate(`/members/${stateUserId}`, { replace: true });
        }
    }, [memberId, navigate, stateUserId]);

    const loadMemberData = useCallback(async (yearId) => {
        Toast.show({ icon: 'loading', duration: 0 });
        try {
            const res = await memberApi.getInfo(userId, yearId);
            const data = res.data.data;
            setUserInfo(data);

            let gallery = data?.memberImg?.filter(item => item.status === 0)?.map(item => item.image) || [];
            if (gallery.length === 0 && data.image) {
                gallery.push(data.image);
            }
            setMemberImages(gallery);
            setActiveImageIndex(0);
            Toast.clear();
        } catch (e) {
            Toast.show({ icon: 'fail', content: "数据同步失败" });
            Toast.clear();
        }
    }, [userId]);

    const loadSeasonList = useCallback(async () => {
        const res = await seasonApi.getList();
        const data = res.data.data;
        if (data?.length > 0) {
            setYearsAction(data);
            const first = data.find(item => item.isFirst === 1) || data[0];
            setYearsButton({ text: first.seasonName, value: first.id });
        }
    }, []);

    useEffect(() => {
        if (!userId) {
            Toast.show({ icon: 'fail', content: '用户信息获取失败' });
            navigate(-1);
            return;
        }
        loadMemberData();
        loadSeasonList();
    }, [loadMemberData, loadSeasonList, navigate, userId]);

    useEffect(() => {
        if (!userId) return;
        // 个人页只取最近十条关联喜事，更多内容交给科朋喜事页面继续分页加载。
        happyEventApi.getList({ current: 1, size: 10, memberId: userId, status: 0 })
            .then(response => {
                const pageData = response.data?.data || {};
                setRelatedHappyEvents(Array.isArray(pageData.records) ? pageData.records : []);
                setRelatedHappyTotal(Number(pageData.total || 0));
            })
            .catch(() => {
                // 喜事属于附加内容，加载失败时不阻断个人资料主体展示。
                setRelatedHappyEvents([]);
                setRelatedHappyTotal(0);
            });
    }, [userId]);

    useEffect(() => {
        const container = scrollerRef.current;
        const activeItem = container?.querySelector('.mi-avatar-item-v4.active');
        if (activeItem && container) {
            const scrollX = activeItem.offsetLeft - (container.offsetWidth / 2) + (activeItem.offsetWidth / 2);
            container.scrollTo({ left: scrollX, behavior: 'smooth' });
        }
    }, [activeImageIndex, memberImages]);

    useEffect(() => {
        if (memberImages.length <= 1 || imageViewerVisible) return undefined;
        // 多张个人头像自动循环播放；手动点选后重新计时，打开大图时暂停轮播。
        const timer = window.setTimeout(() => {
            setActiveImageIndex(previous => (previous + 1) % memberImages.length);
        }, 3600);
        return () => window.clearTimeout(timer);
    }, [activeImageIndex, imageViewerVisible, memberImages.length]);

    const pendingAvatarCount = Number(userInfo?.pendingAvatarCount || 0);
    const showAvatarAuditBadge = Number(adminInfo?.admin) === 1
        && String(userInfo?.id || '') === String(adminInfo?.id || '')
        && pendingAvatarCount > 0;

    const adminPanelItems = [
        {
            label: '头像审核',
            icon: <UserOutline />,
            color: 'blue',
            badge: showAvatarAuditBadge ? pendingAvatarCount : 0,
            onClick: () => navigate('/avatarAudit')
        },
        // { label: '等级修正', icon: <UserContactOutline />, color: 'orange', onClick: () => navigate('/levelAudit') },
        // 管理员从当前球员档案直达默认位置编辑，列表页仍可切换其他球员。
        { label: '默认位置', icon: <UserContactOutline />, color: 'cyan', onClick: () => navigate('/positionAudit', { state: { memberId: userInfo?.id } }) },
        { label: '赛季管理', icon: <HistogramOutline />, color: 'blue', onClick: () => navigate('/customSeason') },
        { label: '资料编辑', icon: <UserSetOutline />, color: 'orange', onClick: () => navigate('/changeUser') },
        {
            label: '出勤导出', icon: <span className="mi-action-emoji">📥</span>, color: 'cyan', onClick: () => {
                setAttendanceVisible(true);
            }
        },
        {
            label: '退出系统', icon: <ChatWrongOutline />, color: 'red', onClick: () => {
                Dialog.confirm({ title: '确认退出', content: '登出当前系统账号？', onConfirm: () => { signOut(); navigate('/login') } })
            }
        }
    ];

    const memberPanelItems = [
        { label: '资料编辑', icon: <UserSetOutline />, color: 'orange', onClick: () => navigate('/changeUser') },
        {
            label: '退出系统', icon: <ChatWrongOutline />, color: 'red', onClick: () => {
                Dialog.confirm({ title: '确认退出', content: '确定登出系统？', onConfirm: () => { signOut(); navigate('/login') } })
            }
        }
    ];

    if (!userInfo) return null;

    const profileArea = userInfo?.area || '未知地区';
    const isSelfProfile = String(userInfo?.id) === String(adminInfo?.id);
    const canViewPrivateInfo = Number(adminInfo?.admin) === 1 || isSelfProfile;
    const profileMetrics = [
        { label: '年龄', value: `${userInfo?.age ?? '-'} 岁` },
        { label: '身高', value: `${userInfo?.height ?? '-'} cm` },
        { label: '体重', value: `${userInfo?.weight ?? '-'} kg` },
    ];
    const recentMediaList = Array.isArray(userInfo?.recentMediaList) ? userInfo.recentMediaList : [];
    const recentMediaTotal = Number(userInfo?.recentMediaTotal ?? recentMediaList.length);
    const gameMediaMap = userInfo?.gameMediaMap || {};

    const openMemberMediaPage = () => {
        navigate('/album', {
            state: {
                memberId: userInfo?.id,
                memberName: userInfo?.name
            }
        });
    };

    const openMemberHappyPage = (happyId) => {
        navigate('/happyEvent', {
            state: {
                happyId,
                memberId: userInfo?.id,
                memberName: userInfo?.name
            }
        });
    };

    const formatMediaDate = (uploadTime) => {
        if (!uploadTime) return '';
        return String(uploadTime).replace('T', ' ').slice(0, 16);
    };

    const getMediaDisplayTitle = (media) => {
        if (!media) return '媒体详情';
        return media.remark || media.title || media.fileName || (Number(media.mediaType) === 2 ? '视频记录' : '图片记录');
    };

    const getMediaTagNames = (media) => {
        if (Array.isArray(media?.tagNames) && media.tagNames.length > 0) return media.tagNames;
        return media?.tagName ? String(media.tagName).split(',').map(item => item.trim()).filter(Boolean) : [];
    };

    const openMediaPreview = (media) => {
        const optimisticViewCount = Number(media?.viewCount || 0) + 1;
        setMediaPreviewItem({ ...media, viewCount: optimisticViewCount });

        // 个人页首次只加载缩略数据，点击后再获取与球队相册一致的完整详情。
        axios.get(API_ROUTES.album_detail, {
            params: { mediaId: media.id, currentMemberId: adminInfo?.id }
        }).then(response => {
            if (response.data?.code === 200 && response.data?.data) {
                setMediaPreviewItem(previous => previous?.id === media.id
                    ? { ...previous, ...response.data.data, viewCount: previous.viewCount }
                    : previous);
            }
        }).catch(() => {
            // 详情补充失败时继续展示已经随个人资料返回的基础媒体信息。
        });

        axios.post(API_ROUTES.album_view, null, {
            params: { mediaId: media.id }
        }).then(response => {
            if (response.data?.code === 200 && response.data?.data !== undefined) {
                setMediaPreviewItem(previous => previous?.id === media.id
                    ? { ...previous, viewCount: Number(response.data.data) }
                    : previous);
            }
        }).catch(() => {
            // 浏览量记录失败不阻断媒体查看。
        });
    };

    const renderMediaThumbnail = (media, compact = false) => {
        const isVideo = Number(media?.mediaType) === 2;
        const mediaUrl = media?.mediaUrl || '';
        const thumbnailUrl = isVideo
            ? `${mediaUrl}${mediaUrl.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_1000,m_fast`
            : `${mediaUrl}${Icon.ImageFooter || ''}`;
        return (
            <button
                type="button"
                key={media.id}
                className={`mi-media-thumb ${compact ? 'compact' : ''}`}
                onClick={(event) => {
                    event.stopPropagation();
                    openMediaPreview(media);
                }}
            >
                <img src={thumbnailUrl} alt={media.remark || userInfo?.name || '球员媒体'} loading="lazy" />
                {isVideo && <span className="mi-media-play">▶</span>}
                {!compact && (
                    <span className="mi-media-thumb-meta">
                        <strong>{isVideo ? '视频' : '照片'}</strong>
                        <small>{formatMediaDate(media.uploadTime)}</small>
                    </span>
                )}
            </button>
        );
    };

    const getHappyCoverUrl = (item) => {
        const cover = item?.coverMedia || item?.mediaList?.[0];
        const mediaUrl = cover?.mediaUrl || '';
        if (!mediaUrl) return '';
        return Number(cover?.mediaType) === 2
            ? `${mediaUrl}${mediaUrl.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_1000,m_fast`
            : `${mediaUrl}${Icon.ImageFooter || ''}`;
    };

    const getHappyTypeIcon = (typeName) => {
        const name = String(typeName || '');
        if (name.includes('结婚')) return '💍';
        if (name.includes('生子') || name.includes('宝宝')) return '🍼';
        if (name.includes('文章')) return '📄';
        if (name.includes('课题')) return '🧪';
        if (name.includes('表彰') || name.includes('获奖')) return '🏅';
        return '✨';
    };

    const toggleAttendanceMonth = (monthValue) => {
        // 当前月数据尚未完整，当前月和未来月份都不允许选择。
        if (monthValue >= currentAttendanceMonth) return;
        setSelectedAttendanceMonths(previous => {
            if (previous.includes(monthValue)) {
                return previous.filter(item => item !== monthValue);
            }
            if (previous.length >= 24) {
                Toast.show({ icon: 'fail', content: '单次最多选择24个月' });
                return previous;
            }
            return [...previous, monthValue].sort();
        });
    };

    const selectRecentAttendanceMonths = (count) => {
        const now = new Date();
        const values = Array.from({ length: count }, (_, index) => {
            const date = new Date(now.getFullYear(), now.getMonth() - index - 1, 1);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        });
        setSelectedAttendanceMonths(values.sort());
        setAttendanceYear(now.getFullYear());
    };

    const exportAttendance = async () => {
        if (selectedAttendanceMonths.length === 0) {
            Toast.show({ icon: 'fail', content: '请至少选择一个月份' });
            return;
        }
        setAttendanceExporting(true);
        Toast.show({ icon: 'loading', duration: 0, content: '正在生成出勤表' });
        try {
            const months = [...selectedAttendanceMonths].sort();
            const response = await memberApi.exportAttendance(months);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `科朋出勤统计_${months[0]}_${months[months.length - 1]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
            Toast.clear();
            Toast.show({ icon: 'success', content: `已导出 ${months.length} 个月` });
            setAttendanceVisible(false);
        } catch (error) {
            Toast.clear();
            Toast.show({ icon: 'fail', content: error?.response?.status === 403 ? '仅管理员可以导出' : '出勤表生成失败' });
        } finally {
            setAttendanceExporting(false);
        }
    };

    return (
        <div className="member-info-container">
            {/* FLOATING ACTIONS */}
            <div className="mi-toolbar-v4">
                <div className="mi-btn-v4" onClick={() => setYearsVisible(true)} style={{ color: '#165DFF' }}>
                    <CalendarOutline fontSize={20} />
                </div>
                {(Number(adminInfo?.admin) === 1 || adminInfo?.captain === 1 || userInfo?.id === adminInfo?.id) && (
                    <div className="mi-btn-v4" onClick={() => setAdminVisible(true)} style={{ color: '#165DFF' }}>
                        <UserSetOutline fontSize={20} />
                        {showAvatarAuditBadge && (
                            <span className="mi-toolbar-badge">{pendingAvatarCount > 99 ? '99+' : pendingAvatarCount}</span>
                        )}
                    </div>
                )}
            </div>


            {/* 新版个人档案头图：照片、身份与基础资料分层展示。 */}
            <div className="mi-hero-v4">
                <div className="mi-hero-bg-grid" />
                <div className="mi-hero-shine" />
                <div className="mi-marquee-v4" ref={scrollerRef}>
                    <div className="mi-marquee-spacer" />
                    {memberImages.map((img, idx) => (
                        <div
                            key={idx}
                            className={`mi-avatar-item-v4 ${idx === activeImageIndex ? 'active' : ''}`}
                            aria-current={idx === activeImageIndex ? 'true' : undefined}
                            aria-label={`查看第 ${idx + 1} 张个人头像`}
                            onClick={() => {
                                setActiveImageIndex(idx);
                                if (idx === activeImageIndex) setImageViewerVisible(true);
                            }}
                        >
                            <img src={img} className="mi-avatar-img-v4" alt="p" />
                        </div>
                    ))}
                    {memberImages.length === 0 && (
                        <div className="mi-avatar-item-v4 mi-avatar-placeholder active">👤</div>
                    )}
                    <div className="mi-marquee-spacer" />
                </div>

                <div className="mi-hud-v4">
                    <div className="mi-hud-main-v4">
                        <div className="mi-hud-number-block">
                            <span className="mi-hud-number-symbol">NO.</span>
                            <span className="mi-hud-num-v4">{userInfo?.memberNum || '--'}</span>
                        </div>
                        <div className="mi-hud-identity">
                            <div className="mi-hud-kicker">科朋球员档案</div>
                            <div className="mi-hud-name-row">
                                <span className="mi-hud-name-v4">{userInfo?.name}</span>
                            </div>
                            <div className="mi-hud-area">📍 {profileArea}</div>
                        </div>
                    </div>
                    <div className="mi-profile-chip-row">
                        {profileMetrics.map(item => (
                            <div className="mi-profile-chip" key={item.label}>
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 我的媒体放在私人资料上方，既突出个人内容，也不打断赛季统计信息。 */}
            {recentMediaList.length > 0 && (
                <div className="mi-card-v4 mi-media-card">
                    <div className="mi-media-section-head">
                        <div className="mi-card-v4-title">我的精彩瞬间</div>
                        <div className="mi-media-section-actions">
                            <span>{recentMediaTotal} 条媒体</span>
                            <button type="button" onClick={openMemberMediaPage}>查看详情</button>
                        </div>
                    </div>
                    <div className="mi-recent-media-grid">
                        {recentMediaList.map(media => renderMediaThumbnail(media))}
                        {recentMediaTotal > recentMediaList.length && (
                            <button type="button" className="mi-media-more" onClick={openMemberMediaPage}>
                                <span>＋</span>
                                <strong>查看更多</strong>
                                <small>还有 {recentMediaTotal - recentMediaList.length} 条</small>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* 与当前球员关联的喜事紧跟精彩瞬间展示，点击条目直接打开对应详情。 */}
            {relatedHappyEvents.length > 0 && (
                <div className="mi-card-v4 mi-happy-card">
                    <div className="mi-happy-section-head">
                        <div className="mi-card-v4-title">我的科朋喜事</div>
                        <div className="mi-happy-section-actions">
                            <span>{relatedHappyTotal} 条喜事</span>
                            <button type="button" onClick={() => openMemberHappyPage()}>查看全部</button>
                        </div>
                    </div>
                    <div className="mi-happy-list">
                        {relatedHappyEvents.map(item => {
                            const coverUrl = getHappyCoverUrl(item);
                            const typeName = item.eventTypeName || '科朋喜事';
                            return (
                                <button type="button" className="mi-happy-item" key={item.id} onClick={() => openMemberHappyPage(item.id)}>
                                    <div className={`mi-happy-cover ${coverUrl ? '' : 'empty'}`}>
                                        {coverUrl
                                            ? <img src={coverUrl} alt={item.title || typeName} loading="lazy" />
                                            : <span>{getHappyTypeIcon(typeName)}</span>}
                                    </div>
                                    <div className="mi-happy-body">
                                        <div className="mi-happy-title-row">
                                            <strong>{item.title || '科朋喜事'}</strong>
                                            <em>{getHappyTypeIcon(typeName)} {typeName}</em>
                                        </div>
                                        <p>{item.content || '点击查看喜事详情'}</p>
                                        <div className="mi-happy-meta">
                                            {item.eventDate && <span>📅 {item.eventDate}</span>}
                                            {item.gameName && <span>⚽ {item.gameName}</span>}
                                        </div>
                                    </div>
                                    <i className="mi-happy-arrow">›</i>
                                </button>
                            );
                        })}
                    </div>
                    {relatedHappyTotal > relatedHappyEvents.length && (
                        <button type="button" className="mi-happy-more" onClick={() => openMemberHappyPage()}>
                            查看其余 {relatedHappyTotal - relatedHappyEvents.length} 条相关喜事 <span>›</span>
                        </button>
                    )}
                </div>
            )}

            {/* 生日和手机号只在本人或管理员视角渲染，后端接口同时执行脱敏。 */}
            {canViewPrivateInfo && (
                <div className="mi-private-card">
                    <div className="mi-private-head">
                        <div>
                            <strong>私人资料</strong>
                            <span>仅管理员和本人可见</span>
                        </div>
                        <em>🔒</em>
                    </div>
                    <div className="mi-private-grid">
                        <div className="mi-private-item">
                            <span className="mi-private-icon birthday">🎂</span>
                            <div>
                                <small>生日</small>
                                <strong>{formatProfileBirthday(userInfo?.birthday)}</strong>
                            </div>
                        </div>
                        <div className="mi-private-item">
                            <span className="mi-private-icon mobile">☎</span>
                            <div>
                                <small>电话号码</small>
                                <strong>{userInfo?.mobile || '未填写'}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SUMMARY - STRICT COLORS */}
            <div className="mi-card-v4">
                <div className="mi-card-v4-title">赛季战绩统计 ({yearsButton.text})</div>
                <div className="mi-summary-v4">
                    <div className="mi-sum-item">
                        <div className="mi-sum-val c-win">{userInfo?.successCount ?? 0}</div>
                        <div className="mi-sum-lbl">W / 胜利</div>
                    </div>
                    <div className="mi-sum-item">
                        <div className="mi-sum-val c-draw">{userInfo?.drawCount ?? 0}</div>
                        <div className="mi-sum-lbl">D / 平局</div>
                    </div>
                    <div className="mi-sum-item">
                        <div className="mi-sum-val c-loss">{userInfo?.failCount ?? 0}</div>
                        <div className="mi-sum-lbl">L / 失败</div>
                    </div>
                </div>
            </div>

            {/* GRID STATS */}
            <div className="mi-card-v4">
                <div className="mi-card-v4-title">个人核心效率表现</div>
                <div className="mi-grid-v4">
                    {[
                        { k: '进球', v: userInfo?.goalCount ?? 0 },
                        { k: '助攻', v: userInfo?.assistingCount ?? 0 },
                        { k: '出场', v: userInfo?.totalGame ?? 0 },
                        { k: '积分', v: userInfo?.integral ?? 0 },
                        { k: '时间', v: `${Math.round(userInfo?.playTime / 60)}'` },
                        { k: '进攻', v: userInfo?.attackGoal ?? 0 },
                        { k: '防守', v: userInfo?.defendGoal ?? 0 },
                        { k: '参赛', v: userInfo?.totalAttendance ?? 0 },
                    ].map((s, i) => (
                        <div key={i} className="mi-grid-cell">
                            <div className="mi-grid-val">{s.v}</div>
                            <div className="mi-grid-lbl">{s.k}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* DISCIPLINE */}
            <div className="mi-card-v4">
                <div className="mi-card-v4-title">赛场纪律统计</div>
                <div className="mi-card-row-v4">
                    <div className="mi-card-pair">
                        <div className="mi-card-icon yellow" />
                        <div className="mi-card-count">{userInfo?.yellowCard ?? 0}</div>
                    </div>
                    <div className="mi-card-pair">
                        <div className="mi-card-icon red" />
                        <div className="mi-card-count">{userInfo?.redCard ?? 0}</div>
                    </div>
                </div>
            </div>

            {/* CAREER HISTORY */}
            <div className="mi-card-v4">
                <div className="mi-card-v4-title">科朋数字生涯（2017-）</div>
                <div className="mi-summary-v4">
                    <div className="mi-sum-item">
                        <div className="mi-sum-val" style={{ color: '#165DFF' }}>{Number(userInfo?.attendance) + Number(userInfo?.historyCount)}</div>
                        <div className="mi-sum-lbl">总参赛</div>
                    </div>
                    <div className="mi-sum-item">
                        <div className="mi-sum-val" style={{ color: '#165DFF' }}>{Number(userInfo?.gameCount) + Number(userInfo?.historyCount)}</div>
                        <div className="mi-sum-lbl">总出场</div>
                    </div>
                    <div className="mi-sum-item">
                        <div className="mi-sum-val" style={{ color: '#165DFF' }}>{userInfo.joinDate?.substring(0, 4) || '2017'}</div>
                        <div className="mi-sum-lbl">入队时间</div>
                    </div>
                </div>
            </div>

            {/* ====== FULL MATCH HISTORY (No Slicing) ====== */}
            <div className="mi-card-v4">
                <div className="mi-card-v4-title">出勤详情</div>
                <div className="mi-history-container">
                    {userInfo?.scoreList?.length > 0 ? (
                        userInfo?.scoreList?.map((m, i) => {
                            // Color Class Logic: Win=Red, Loss=Green, Draw=Gray
                            // Treat 'Referee' and 'Leave' as non-participation (leave style)
                            const isPlayed = m.pos === '上场球员';
                            const resType = (!isPlayed) ? 'leave' : (String(m.record) === '1' ? 'win' : String(m.record) === '2' ? 'loss' : 'draw');
                            const resChar = resType === 'leave' ? 'R' : resType.charAt(0).toUpperCase();
                            const matchMediaList = Array.isArray(gameMediaMap?.[m.id]) ? gameMediaMap[m.id] : [];

                            return (
                                <div className={`mi-match-card-v4 bg-${resType}-glass`} key={i} onClick={() => navigate(`/games/${m.id}/history`)}>
                                    <div className="mi-match-main">
                                        <div className={`mi-res-circle-v4 bg-${resType}`}>
                                            {resChar}
                                        </div>
                                        <div className="mi-match-body">
                                            <div className="mi-match-top">{m.gameName}</div>
                                            <div className="mi-match-date">{m.startDate}</div>
                                            <div className="mi-role-tag">
                                                {isPlayed ? (m.position || '正式球员') : (m.pos || '未知状态')}
                                            </div>
                                        </div>
                                        <div className="mi-match-score">
                                            {isPlayed ? (m.score || '-') : (m.pos || '-')}
                                        </div>
                                    </div>
                                    {matchMediaList.length > 0 && (
                                        <div className="mi-match-media-block">
                                            <div className="mi-match-media-title">
                                                <span>与我相关的媒体</span>
                                                <small>{matchMediaList.length} 条</small>
                                            </div>
                                            <div className="mi-match-media-strip">
                                                {matchMediaList.map(media => renderMediaThumbnail(media, true))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : <Empty description="暂无历史比赛数据" />}
                </div>
            </div>

            <div style={{ height: '30px' }} />

            {/* OVERLAYS */}
            <ImageViewer.Multi
                images={memberImages}
                visible={imageViewerVisible}
                defaultIndex={activeImageIndex}
                onClose={() => setImageViewerVisible(false)}
            />

            <Popup
                visible={Boolean(mediaPreviewItem)}
                position="bottom"
                onMaskClick={() => setMediaPreviewItem(null)}
                bodyClassName="mi-media-preview-popup"
                destroyOnClose
            >
                <div className="mi-media-preview-sheet">
                    <div className="mi-media-preview-head">
                        <div>
                            <strong>{getMediaDisplayTitle(mediaPreviewItem)}</strong>
                            <span>{formatMediaDate(mediaPreviewItem?.uploadTime)}</span>
                        </div>
                        <button type="button" onClick={() => setMediaPreviewItem(null)}>关闭</button>
                    </div>
                    <div className="mi-media-preview-stage">
                        {Number(mediaPreviewItem?.mediaType) === 2 ? (
                            <video src={mediaPreviewItem?.mediaUrl} controls autoPlay playsInline />
                        ) : (
                            <img src={mediaPreviewItem?.mediaUrl} alt={mediaPreviewItem?.remark || '球员相关媒体'} />
                        )}
                    </div>
                    <div className="mi-media-preview-details">
                        {mediaPreviewItem?.gameName && (
                            <div className="mi-media-detail-row"><span>比赛</span><strong>{mediaPreviewItem.gameName}</strong></div>
                        )}
                        {mediaPreviewItem?.eventInfo && (
                            <div className="mi-media-detail-row"><span>赛况</span><strong>{mediaPreviewItem.eventInfo}</strong></div>
                        )}
                        {(mediaPreviewItem?.memberNames || mediaPreviewItem?.memberName) && (
                            <div className="mi-media-detail-row"><span>关联队员</span><strong>{mediaPreviewItem.memberNames || mediaPreviewItem.memberName}</strong></div>
                        )}
                        {getMediaTagNames(mediaPreviewItem).length > 0 && (
                            <div className="mi-media-detail-row"><span>标签</span><strong>{getMediaTagNames(mediaPreviewItem).join('、')}</strong></div>
                        )}
                        {mediaPreviewItem?.uploadMemberName && (
                            <div className="mi-media-detail-row"><span>上传人</span><strong>{mediaPreviewItem.uploadMemberName}</strong></div>
                        )}
                        <div className="mi-media-detail-row"><span>媒体类型</span><strong>{Number(mediaPreviewItem?.mediaType) === 2 ? '视频' : '照片'}</strong></div>
                        <div className="mi-media-preview-stats">
                            <span>👁 {Number(mediaPreviewItem?.viewCount || 0)}</span>
                            <span>👍 {Number(mediaPreviewItem?.likeCount || 0)}</span>
                            <span>👎 {Number(mediaPreviewItem?.dislikeCount || 0)}</span>
                            <span>💬 {Number(mediaPreviewItem?.commentCount || 0)}</span>
                        </div>
                    </div>
                </div>
            </Popup>

            <Popup visible={adminVisible} onMaskClick={() => setAdminVisible(false)} bodyStyle={{ borderRadius: '24px 24px 0 0' }}>
                <div className="mi-popup-sheet">
                    <div className="mi-popup-title">系统管理及后勤中心</div>
                    <div className="mi-action-grid">
                        {(Number(adminInfo?.admin) === 1 ? adminPanelItems : memberPanelItems).map((it, idx) => (
                            <div className="mi-action-cell" key={idx} onClick={() => { setAdminVisible(false); it.onClick(); }}>
                                <div className={`mi-action-icon ${it.color}`}>
                                    {it.icon}
                                    {Number(it.badge) > 0 && (
                                        <span className="mi-action-badge">{Number(it.badge) > 99 ? '99+' : it.badge}</span>
                                    )}
                                </div>
                                <div className="mi-action-lbl">{it.label}</div>
                            </div>
                        ))}
                    </div>
                    <button className="mi-btn-full" onClick={() => setAdminVisible(false)}>关闭</button>
                </div>
            </Popup>

            {/* 管理员出勤导出使用独立底部面板，支持跨年份保留多个月份选择。 */}
            <Popup
                visible={attendanceVisible}
                position="bottom"
                onMaskClick={() => !attendanceExporting && setAttendanceVisible(false)}
                bodyClassName="mi-attendance-popup"
                destroyOnClose
            >
                <div className="mi-attendance-sheet">
                    <div className="mi-attendance-header">
                        <button type="button" onClick={() => setAttendanceVisible(false)} disabled={attendanceExporting}>取消</button>
                        <div>
                            <strong>导出出勤统计</strong>
                            <span>每个月份生成一个 Excel Sheet</span>
                        </div>
                        <button
                            type="button"
                            className="primary"
                            disabled={attendanceExporting || selectedAttendanceMonths.length === 0}
                            onClick={exportAttendance}
                        >
                            {attendanceExporting ? '生成中' : '导出'}
                        </button>
                    </div>

                    <div className="mi-attendance-content">
                        <div className="mi-attendance-quick">
                            <button type="button" onClick={() => selectRecentAttendanceMonths(3)}>近3个月</button>
                            <button type="button" onClick={() => selectRecentAttendanceMonths(6)}>近6个月</button>
                            <button type="button" onClick={() => setSelectedAttendanceMonths([])}>清空</button>
                        </div>

                        <div className="mi-attendance-year-nav">
                            <button type="button" aria-label="上一年" onClick={() => setAttendanceYear(year => year - 1)}>‹</button>
                            <strong>{attendanceYear} 年</strong>
                            <button
                                type="button"
                                aria-label="下一年"
                                disabled={attendanceYear >= now.getFullYear()}
                                onClick={() => setAttendanceYear(year => year + 1)}
                            >›</button>
                        </div>

                        <div className="mi-attendance-month-grid">
                            {Array.from({ length: 12 }, (_, index) => {
                                const month = String(index + 1).padStart(2, '0');
                                const value = `${attendanceYear}-${month}`;
                                const selected = selectedAttendanceMonths.includes(value);
                                const disabled = value >= currentAttendanceMonth;
                                return (
                                    <button
                                        type="button"
                                        key={value}
                                        className={selected ? 'selected' : ''}
                                        disabled={disabled}
                                        onClick={() => toggleAttendanceMonth(value)}
                                    >
                                        <span>{index + 1}月</span>
                                        {selected && <i>✓</i>}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mi-attendance-selected">
                            <div>
                                <strong>已选择 {selectedAttendanceMonths.length} 个月</strong>
                                <span>可切换年份继续追加月份</span>
                            </div>
                            <div className="mi-attendance-selected-list">
                                {selectedAttendanceMonths.length > 0
                                    ? selectedAttendanceMonths.map(month => (
                                        <button type="button" key={month} onClick={() => toggleAttendanceMonth(month)}>
                                            {month}<span>×</span>
                                        </button>
                                    ))
                                    : <em>暂未选择月份</em>}
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>

            <Popup visible={yearsVisible} onMaskClick={() => setYearsVisible(false)} bodyStyle={{ borderRadius: '24px 24px 0 0' }}>
                <div className="mi-popup-sheet">
                    <div className="mi-popup-title">切换赛季数据视图</div>
                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {yearsAction.map(y => (
                            <div
                                key={y.id}
                                className={`mi-season-row ${yearsButton.value === y.id ? 'active' : ''}`}
                                onClick={() => {
                                    setYearsVisible(false);
                                    setYearsButton({ text: y.seasonName, value: y.id });
                                    loadMemberData(y.id);
                                }}
                            >
                                <div className="mi-season-row-txt">{y.seasonName}</div>
                                {yearsButton.value === y.id && <CheckOutline style={{ color: '#165DFF', fontSize: '20px' }} />}
                            </div>
                        ))}
                    </div>
                </div>
            </Popup>
        </div>
    );
}
